import { Fade, IconButton, Typography } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import { memo } from 'react'

import { PromotionSkipCount } from '../../graphql/promotions'
import { formatCount } from '../../helpers/promotionAmount'
import { skipReasonLabel } from '../../helpers/skipReasonLabels'
import { getTranslate } from '../../localization'
import useStyles from './styles/mobile.styles'
import { isUnattributedSkip, UNATTRIBUTED_LABEL } from './unattributedSkip'

/**
 * Skips by promotion as label-value cards, for a narrow viewport.
 *
 * The unattributed test and its sentence are IMPORTED, not repeated. Deciding what counts as
 * unattributed is behaviour, and the convention this pair follows says duplicate layout and
 * never behaviour — a rule this file's first version broke while quoting it. `OverviewTable`
 * exports `STATUS_LABEL` for its own mobile twin for exactly this reason.
 *
 * The full sentence is kept rather than shortened. A narrow screen is where an operator is
 * most likely to read only this one card, so the row that has to explain itself must keep
 * explaining itself.
 */
interface Props {
  rows: PromotionSkipCount[]
  onRefresh: () => void
}

const SkipPromotionListMobile = ({ rows, onRefresh }: Props) => {
  const classes = useStyles()

  return (
    <div data-cy="SkipPromotionsMobile">
      <div className={classes.listHeader}>
        <IconButton
          size="small"
          onClick={onRefresh}
          data-cy="SkipPromotionsRefreshMobile"
          aria-label={getTranslate('Refresh')}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </div>
      {rows.map((row) => {
        const unattributed = isUnattributedSkip(row)
        return (
          <Fade in timeout={600} key={`${row.promotionId}:${row.reason}`}>
            <div
              className={classes.card}
              data-cy={
                unattributed
                  ? 'SkipUnattributedCardMobile'
                  : 'SkipPromotionCardMobile'
              }>
              <Typography variant="body2" className={classes.cardLabel}>
                {getTranslate('Promotion')}
              </Typography>
              <Typography
                variant="body1"
                className={unattributed ? classes.unattributed : ''}>
                {unattributed ? getTranslate(UNATTRIBUTED_LABEL) : row.name}
              </Typography>

              <Typography variant="body2" className={classes.cardLabel}>
                {getTranslate('Reason')}
              </Typography>
              <Typography variant="body1" data-reason={row.reason}>
                {getTranslate(skipReasonLabel(row.reason))}
              </Typography>

              <Typography variant="body2" className={classes.cardLabel}>
                {getTranslate('Skips')}
              </Typography>
              <Typography variant="body1">{formatCount(row.count)}</Typography>
            </div>
          </Fade>
        )
      })}
    </div>
  )
}

export default memo(SkipPromotionListMobile)
